import { Button, ButtonProps, Icon, IconProps } from '@chakra-ui/react';
import React, { ElementType } from 'react';

export interface MCustomIconButtonProps extends ButtonProps {
  /**
   * Required accessible name for this icon-only button. Screen readers have no
   * text to announce otherwise, and it doubles as the `getByRole('button', {
   * name })` hook our tests rely on. Mirrors Chakra's `IconButton`, which also
   * requires `aria-label`.
   */
  'aria-label': string;
  containerSize?: number | string;
  btnSize?: number | string;
  icon: ElementType;
  iconProps?: Omit<IconProps, 'css'>;
  iconStyle?: any;
  isLoading?: boolean;
  /**
   * Optional override for the icon glyph color. Leave unset to inherit from
   * the Button variant (`icon` variant uses `fg.strong` with `bg.hover` on
   * hover — the design-system default). Pass an explicit token only when the
   * icon needs an intent color (status, branded action, on-dark-bg, etc.).
   */
  iconColor?: string;
  /**
   * Optional override for the icon glyph color on hover. Leave unset to let
   * the Button variant's `_hover.color` drive it (the icon picks up the
   * button's `color` via `currentColor`).
   */
  iconColorHover?: string;
  /**
   * Optional override for the button's hover background. Leave unset to use
   * the Button variant's `_hover.background` (`bg.hover` on the `icon`
   * variant) — this matches the rest of the design system.
   */
  buttonColorHover?: string;
  isDisabled?: boolean;
}

/*
 * Thin wrapper over Chakra `Button` that renders an `Icon` child. Color,
 * hover bg, focus halo, and disabled state are delegated to the Button
 * theme's `icon` variant — see `styles/chakra/components/Button.ts`. Don't
 * add inline color overrides here; lift them into the theme so every icon
 * button in the app stays consistent.
 */
const MCustomIconButton = React.forwardRef<any, MCustomIconButtonProps>(
  (
    {
      btnSize = 4,
      containerSize = 5,
      icon,
      iconProps,
      iconStyle,
      isLoading = false,
      iconColor,
      iconColorHover,
      buttonColorHover,
      isDisabled,
      disabled,
      ...rest
    }: MCustomIconButtonProps,
    ref,
  ) => {
    // Only attach an inline `_hover` if a caller explicitly opted into a
    // custom hover bg — otherwise the theme variant's `_hover` applies.
    const hoverOverride = buttonColorHover
      ? { _hover: { backgroundColor: buttonColorHover } }
      : {};
    return (
      <Button
        ref={ref}
        variant="icon"
        w={containerSize}
        h={containerSize}
        loading={isLoading}
        disabled={disabled ?? isDisabled}
        padding="0"
        minWidth="initial"
        {...hoverOverride}
        {...rest}
      >
        <Icon
          as={icon}
          w={btnSize}
          h={btnSize}
          color={iconColor}
          style={iconStyle}
          _hover={iconColorHover ? { color: iconColorHover } : undefined}
          {...iconProps}
        />
      </Button>
    );
  },
);

export default MCustomIconButton;
